import React, { useEffect, useRef } from 'react';
import './RobotMascot.scss';

// Animated SVG robot mascot.
// moods: 'idle' | 'wave' | 'talk' | 'think' | 'happy' | 'point'
// Eyes track the cursor via CSS custom properties (--eye-x / --eye-y)
// set directly on the SVG node, so tracking never triggers a re-render.
function RobotMascot({ mood = 'idle', size = 250, onClick }) {
  const svgRef = useRef(null);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    let raf = null;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (!svgRef.current) return;
        const rect = svgRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height * 0.3; // eye line
        const dx = Math.max(-1, Math.min(1, (e.clientX - cx) / (rect.width * 1.4)));
        const dy = Math.max(-1, Math.min(1, (e.clientY - cy) / (rect.height * 1.4)));
        svgRef.current.style.setProperty('--eye-x', `${(dx * 4).toFixed(2)}px`);
        svgRef.current.style.setProperty('--eye-y', `${(dy * 3).toFixed(2)}px`);
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const happy = mood === 'happy';

  return (
    <svg
      ref={svgRef}
      className={`robot robot--${mood}`}
      width={size}
      height={size * 1.1}
      viewBox="0 0 220 242"
      onClick={onClick}
      role="img"
      aria-label="Assistant robot"
    >
      <defs>
        <linearGradient id="robotShell" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#cbd5e1" />
        </linearGradient>
        <linearGradient id="robotBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f1f5f9" />
          <stop offset="100%" stopColor="#b6c2d4" />
        </linearGradient>
        <radialGradient id="robotThruster" cx="0.5" cy="0.35" r="0.65">
          <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="robotCore" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="45%" stopColor="var(--accent, #6366f1)" />
          <stop offset="100%" stopColor="var(--accent, #6366f1)" stopOpacity="0.55" />
        </radialGradient>
      </defs>

      {/* ground shadow */}
      <ellipse className="robot__shadow" cx="110" cy="230" rx="48" ry="8" fill="#312e81" opacity="0.16" />

      <g className="robot__float">
        {/* thruster glow */}
        <ellipse className="robot__thruster" cx="110" cy="196" rx="26" ry="16" fill="url(#robotThruster)" />

        {/* arms */}
        <g className="robot__arm robot__arm--left">
          <rect x="56" y="136" width="16" height="42" rx="8" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="1.5" />
          <circle cx="64" cy="178" r="9" fill="url(#robotShell)" stroke="#94a3b8" strokeWidth="1.5" />
        </g>
        <g className="robot__arm robot__arm--right">
          <rect x="148" y="136" width="16" height="42" rx="8" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="1.5" />
          <circle cx="156" cy="178" r="9" fill="url(#robotShell)" stroke="#94a3b8" strokeWidth="1.5" />
        </g>

        {/* body */}
        <rect x="70" y="128" width="80" height="62" rx="24" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="1.5" />
        <circle className="robot__core" cx="110" cy="158" r="13" fill="url(#robotCore)" />
        <circle cx="110" cy="158" r="13" fill="none" stroke="#94a3b8" strokeWidth="1.5" />

        {/* neck */}
        <rect x="100" y="114" width="20" height="16" rx="6" fill="#b6c2d4" />

        {/* head */}
        <g className="robot__head">
          {/* antenna */}
          <line x1="110" y1="34" x2="110" y2="18" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
          <circle className="robot__antenna-tip" cx="110" cy="13" r="6" fill="var(--accent, #6366f1)" />

          {/* ears */}
          <rect x="44" y="62" width="12" height="28" rx="6" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="1.5" />
          <rect x="164" y="62" width="12" height="28" rx="6" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="1.5" />

          {/* skull */}
          <rect x="54" y="32" width="112" height="86" rx="28" fill="url(#robotShell)" stroke="#94a3b8" strokeWidth="1.5" />

          {/* visor */}
          <rect x="68" y="50" width="84" height="52" rx="17" fill="#0b1220" />
          <rect x="68" y="50" width="84" height="52" rx="17" fill="none" stroke="#1e293b" strokeWidth="1.5" />

          {/* face */}
          <g className="robot__face">
            <g className="robot__eyes">
              {happy ? (
                <>
                  <path d="M84 78 Q92 68 100 78" fill="none" stroke="#7df3ff" strokeWidth="4" strokeLinecap="round" />
                  <path d="M120 78 Q128 68 136 78" fill="none" stroke="#7df3ff" strokeWidth="4" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <g className="robot__eye">
                    <circle cx="92" cy="72" r="7.5" fill="#7df3ff" />
                    <circle cx="94.5" cy="69.5" r="2.4" fill="#ffffff" opacity="0.9" />
                  </g>
                  <g className="robot__eye">
                    <circle cx="128" cy="72" r="7.5" fill="#7df3ff" />
                    <circle cx="130.5" cy="69.5" r="2.4" fill="#ffffff" opacity="0.9" />
                  </g>
                </>
              )}
            </g>
            {happy ? (
              <path className="robot__smile" d="M100 88 Q110 97 120 88" fill="none" stroke="#7df3ff" strokeWidth="3.5" strokeLinecap="round" />
            ) : (
              <rect className="robot__mouth" x="101" y="87" width="18" height="5" rx="2.5" fill="#7df3ff" />
            )}
          </g>
        </g>

        {/* thinking dots */}
        <g className="robot__think-dots">
          <circle cx="172" cy="34" r="4" fill="#94a3b8" />
          <circle cx="184" cy="22" r="5.5" fill="#aab6c8" />
          <circle cx="199" cy="8" r="7" fill="#c3cdd9" />
        </g>
      </g>
    </svg>
  );
}

export default RobotMascot;
